import {
  Add,
  Cancel,
  CheckCircle,
  ContentCut,
  EventBusyOutlined,
  ReportGmailerrorred,
  TaskAlt,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import * as yup from 'yup';

import { AutocompleteCheckboxesCountry } from 'entities/country';

import {
  Country,
  ENextRound,
  ENextRoundVariant,
  ETypeVaccine,
  EUserRole,
  EVaccineType,
  PharmaceuticalCompanyLightItem,
  TVaccineRound,
  Vaccine,
  VaccineDiseaseRef,
} from 'shared/api';
import { sanitizeRound, useCheckCodeUnique } from 'shared/lib';
import { AutocompleteCountry, SelectInput } from 'shared/ui';

const normalizeAgeShedulePeriodic = (ageShedule: Vaccine['age_shedule']): Vaccine['age_shedule'] =>
  (ageShedule ?? []).map((period) => ({
    ...period,
    rounds: (period.rounds ?? []).map((r, i) =>
      i === period.rounds.length - 1 ? r : { ...r, isPeriodicRevactination: false },
    ),
  }));

const validationSchema = yup.object({
  name: yup.string().required('Обязательное поле'),
  code: yup.string().required('Обязательное поле'),
  manufacturerCountry: yup.string().required('Обязательное поле'),
  manufacturer: yup.string().required('Обязательное поле'),
  availableCountries: yup.array().of(yup.string()),
  age_shedule: yup.array().of(
    yup.object().shape({
      start: yup.string().required('Обязательное поле'),
      end: yup.string().required('Обязательное поле'),
      rounds: yup.array().of(
        yup.object().shape({
          type_round: yup
            .mixed<ENextRoundVariant>()
            .oneOf(Object.values(ENextRoundVariant))
            .required('Обязательное поле'),
          count_days: yup.number().notRequired(),
          count_days2: yup.number().notRequired(),
          count_years: yup.number().notRequired(),
          type_vaccination: yup.mixed<ETypeVaccine>().oneOf(Object.values(ETypeVaccine)).required('Обязательное поле'),
        }),
      ),
    }),
  ),
  verified: yup.boolean(),
  descriptions: yup.string(),
  descriptions_en: yup.string(),
  notes: yup.string(),
});

const initialRound: TVaccineRound = {
  type_round: ENextRoundVariant.AnyDays,
  count_days: undefined,
  count_days2: undefined,
  count_years: undefined,
  type_vaccination: ETypeVaccine.Vaccination,
  isPeriodicRevactination: false,
};

const initialRoundReVaccination: TVaccineRound = {
  ...initialRound,
  type_vaccination: ETypeVaccine.ReVaccination,
};

const initialValues: Omit<Vaccine, '_id'> = {
  name: '',
  name_ru: '',
  code: '',
  manufacturerCountry: '',
  manufacturer: '',
  pharmaceuticalCompanyId: undefined,
  availableCountries: [],
  vaccine_type: EVaccineType.alive,
  age_shedule: [],
  verified: false,
  descriptions: undefined as unknown as string,
  descriptions_en: undefined as unknown as string,

  pharmacological_properties: undefined as unknown as string,
  compound: undefined as unknown as string,
  indications_for_use: undefined as unknown as string,
  contraindications: undefined as unknown as string,
  mode_of_application: undefined as unknown as string,
  precautions_for_use: undefined as unknown as string,
  carefully: undefined as unknown as string,
  special_instructions: undefined as unknown as string,
  side_effect: undefined as unknown as string,
  use_during_pregnancy_and_breastfeeding: undefined as unknown as string,
  interaction_with_other_drugs: undefined as unknown as string,
  storage_conditions: undefined as unknown as string,
  transportation_conditions: undefined as unknown as string,

  notes: undefined as unknown as string,
};

type VaccineListForValidation = {
  id: string;
  name: string;
  manufacturer: string;
  manufacturerCountry: string;
};

type EmptyPeriodInfo = {
  index: number;
  start: number | string;
  end: number | string;
};

type EmptyRoundsConfirmState = {
  open: boolean;
  periods: EmptyPeriodInfo[];
  values: Omit<Vaccine, '_id'> | null;
};

type Props = {
  title: string;
  isOpen: boolean;
  isPets?: boolean;
  onClose: () => void;
  onSubmit: Function;
  onVerified?: Function;
  isLoading: boolean;
  userRole?: EUserRole;
  selectedVaccine?: Vaccine;
  vaccineList: VaccineListForValidation[];
  countries: Country[];
  pharmaCompanies: PharmaceuticalCompanyLightItem[];
};

export const VaccineDialog = ({
  title,
  isOpen,
  isPets = false,
  onClose,
  onSubmit,
  onVerified,
  userRole,
  isLoading,
  selectedVaccine,
  vaccineList,
  countries,
  pharmaCompanies,
}: Props) => {
  const [selectedAgeShedule, setSelectedAgeShedule] = useState(0);
  const [selectedRound, setSelectedRound] = useState(0);
  const ageSheduleRef = useRef<HTMLDivElement>(null);
  const [emptyRoundsConfirm, setEmptyRoundsConfirm] = useState<EmptyRoundsConfirmState>({
    open: false,
    periods: [],
    values: null,
  });

  // Определяем режим: создание (true) или редактирование (false)
  const isCreateMode = !selectedVaccine;

  const submitValues = (values: Omit<Vaccine, '_id'>) => {
    const newShedules = values.age_shedule.map((item) => ({
      ...item,
      rounds: item.rounds.map(sanitizeRound),
    }));

    onSubmit({ ...values, age_shedule: newShedules, verified: false });
  };

  const formik = useFormik({
    initialValues: selectedVaccine
      ? { ...selectedVaccine, age_shedule: normalizeAgeShedulePeriodic(selectedVaccine.age_shedule) }
      : initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values: Omit<Vaccine, '_id'>, helpers) => {
      const emptyPeriods = values.age_shedule
        .map((p, index) => ({ index, start: p.start, end: p.end, rounds: p.rounds }))
        .filter((p) => (p.rounds?.length ?? 0) === 0);

      if (emptyPeriods.length > 0) {
        setEmptyRoundsConfirm({
          open: true,
          periods: emptyPeriods.map(({ index, start, end }) => ({ index, start, end })),
          values,
        });
        helpers.setSubmitting(false);
        return;
      }

      submitValues(values);
    },
  });

  const handleEmptyRoundsConfirm = () => {
    if (emptyRoundsConfirm.values) submitValues(emptyRoundsConfirm.values);
    setEmptyRoundsConfirm({ open: false, periods: [], values: null });
  };

  const scrollToAgeShedule = () => {
    setTimeout(() => {
      const node = ageSheduleRef.current;
      if (!node) return;
      const scrollParent = node.closest<HTMLElement>('.MuiDialog-paper');
      if (scrollParent) {
        const targetTop =
          scrollParent.scrollTop + node.getBoundingClientRect().top - scrollParent.getBoundingClientRect().top - 8;
        scrollParent.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
      } else {
        node.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  };

  const handleEmptyRoundsCancel = () => {
    const firstEmpty = emptyRoundsConfirm.periods[0]?.index;
    setEmptyRoundsConfirm({ open: false, periods: [], values: null });
    if (typeof firstEmpty === 'number') {
      setSelectedAgeShedule(firstEmpty);
      scrollToAgeShedule();
    }
  };

  const handleGoToPeriod = (index: number) => {
    setSelectedAgeShedule(index);
    setEmptyRoundsConfirm({ open: false, periods: [], values: null });
    scrollToAgeShedule();
  };

  const codeCheck = useCheckCodeUnique(
    isPets ? 'vaccinePets' : 'vaccine',
    formik.values.code,
    selectedVaccine?._id,
    isOpen && formik.values.code.trim().length > 0,
  );

  const isExistVaccine = (() => {
    const foundVaccine = vaccineList.find(
      ({ name, manufacturer, manufacturerCountry }) =>
        name === formik.values.name &&
        manufacturer === formik.values.manufacturer &&
        manufacturerCountry === formik.values.manufacturerCountry,
    );
    // Если вакцина не найдена - комбинация уникальна
    if (!foundVaccine) return false;
    // Если это та же вакцина (редактирование) - комбинация уникальна
    if (selectedVaccine && foundVaccine.id === selectedVaccine._id) return false;
    // Иначе - комбинация уже занята другой вакциной
    return true;
  })();

  const handleAddAgeShedule = () => {
    const lastPeriod = formik.values.age_shedule.at(-1);
    void formik.setFieldValue('age_shedule', [
      ...formik.values.age_shedule,
      {
        start: lastPeriod?.end ? +lastPeriod?.end + 1 : 0,
        end: lastPeriod?.end ? +lastPeriod?.end + 2 : 36500,
        rounds: [initialRound],
      },
    ]);

    setSelectedAgeShedule(formik.values.age_shedule.length);
  };

  const canInsertAgeShedule = (atIndex: number) => {
    const list = formik.values.age_shedule;
    const prev = list[atIndex - 1];
    const next = list[atIndex];
    if (!prev || !next) return false;
    const prevLen = +prev.end - +prev.start;
    const nextLen = +next.end - +next.start;
    if (prevLen < 2 || nextLen < 2) return false;
    const prevMid = +prev.start + Math.floor(prevLen / 2);
    const nextMid = +next.start + Math.ceil(nextLen / 2);
    return prevMid + 1 <= nextMid - 1;
  };

  const handleInsertAgeShedule = (atIndex: number) => {
    const prev = formik.values.age_shedule[atIndex - 1];
    const next = formik.values.age_shedule[atIndex];
    if (!prev || !next) return;

    const prevLen = +prev.end - +prev.start;
    const nextLen = +next.end - +next.start;

    // Каждый сосед должен иметь хотя бы 2 дня, чтобы отдать половину
    if (prevLen < 2 || nextLen < 2) return;

    // Отрезаем правую половину у левого соседа и левую половину у правого
    const prevMid = +prev.start + Math.floor(prevLen / 2);
    const nextMid = +next.start + Math.ceil(nextLen / 2);

    const newPeriod = {
      start: prevMid + 1,
      end: nextMid - 1,
      rounds: [initialRound],
    };

    // Проверяем, что новый период валиден
    if (newPeriod.start > newPeriod.end) return;

    const updated = [...formik.values.age_shedule.map((s) => ({ ...s }))];
    updated[atIndex - 1] = { ...updated[atIndex - 1], end: prevMid };
    updated[atIndex] = { ...updated[atIndex], start: nextMid };

    updated.splice(atIndex, 0, newPeriod);

    void formik.setFieldValue('age_shedule', updated);
    setSelectedAgeShedule(atIndex);
    setSelectedRound(0);
  };

  const handleSplitAgeShedule = (index: number) => {
    const period = formik.values.age_shedule[index];
    if (!period) return;

    const start = +period.start;
    const end = +period.end;
    if (end - start < 1) return; // нельзя разделить период в 1 день

    const mid = Math.floor((start + end) / 2);
    const firstHalf = { ...period, start, end: mid };
    const secondHalf = { start: mid + 1, end, rounds: [initialRound] };

    const updated = [...formik.values.age_shedule];
    updated.splice(index, 1, firstHalf, secondHalf);

    void formik.setFieldValue('age_shedule', updated);
    setSelectedAgeShedule(index);
    setSelectedRound(0);
  };

  const handleAgeSheduleStartChange = (index: number, value: number) => {
    if (isNaN(value) || value < 0) return;
    const updated = [...formik.values.age_shedule.map((s) => ({ ...s }))];
    updated[index].start = value;
    void formik.setFieldValue('age_shedule', updated);
  };

  const handleAgeSheduleEndChange = (index: number, value: number) => {
    if (isNaN(value) || value < 0) return;
    const updated = [...formik.values.age_shedule.map((s) => ({ ...s }))];
    updated[index].end = value;
    void formik.setFieldValue('age_shedule', updated);
  };

  const handleAgeSheduleStartBlur = (index: number) => {
    const updated = [...formik.values.age_shedule.map((s) => ({ ...s }))];
    const val = +updated[index].start;
    const minStart = index > 0 ? +updated[index - 1].start + 1 : 0;
    const maxStart = +updated[index].end;
    const clamped = isNaN(val) ? minStart : Math.max(minStart, Math.min(maxStart, val));

    updated[index].start = clamped;
    if (index > 0) {
      updated[index - 1] = { ...updated[index - 1], end: clamped - 1 };
    }
    void formik.setFieldValue('age_shedule', updated);
  };

  const handleAgeSheduleEndBlur = (index: number) => {
    const updated = [...formik.values.age_shedule.map((s) => ({ ...s }))];
    const val = +updated[index].end;
    const minEnd = +updated[index].start;
    const maxEnd = index < updated.length - 1 ? +updated[index + 1].end - 1 : Infinity;
    const clamped = isNaN(val) ? minEnd : Math.max(minEnd, Math.min(maxEnd, val));

    updated[index].end = clamped;
    if (index < updated.length - 1) {
      updated[index + 1] = { ...updated[index + 1], start: clamped + 1 };
    }
    void formik.setFieldValue('age_shedule', updated);
  };

  const handleInsertRound = (atIndex: number) => {
    const rounds = formik.values.age_shedule[selectedAgeShedule]?.rounds ?? [];
    const priorIsReVaccination = rounds
      .slice(0, atIndex)
      .some((r) => r.type_vaccination === ETypeVaccine.ReVaccination);
    const seed = priorIsReVaccination ? initialRoundReVaccination : initialRound;

    const newAgeShedules = formik.values.age_shedule.map((item, index) => {
      if (index === selectedAgeShedule) {
        const newRounds = [...item.rounds];
        newRounds.splice(atIndex, 0, seed);
        return { ...item, rounds: newRounds };
      }
      return item;
    });

    void formik.setFieldValue('age_shedule', newAgeShedules);
    setSelectedRound(atIndex);
  };

  const handleAddRound = () => {
    const lastRound = formik.values.age_shedule[selectedAgeShedule]?.rounds.at(-1);
    const seed = lastRound?.type_vaccination === ETypeVaccine.ReVaccination ? initialRoundReVaccination : initialRound;

    const newAgeShedules = formik.values.age_shedule.map((item, index) => {
      if (index === selectedAgeShedule) {
        return { ...item, rounds: [...item.rounds, seed] };
      }

      return item;
    });

    void formik.setFieldValue('age_shedule', newAgeShedules);

    setSelectedRound(formik.values.age_shedule[selectedAgeShedule]?.rounds.length);
  };

  const handleDeleteRound = () => {
    const newAgeShedules = formik.values.age_shedule.map((item, index) => {
      if (index !== selectedAgeShedule) return item;
      const filtered = item.rounds.filter((_, i) => i !== selectedRound);
      // First round cannot be ReVaccination — flip it to Vaccination
      const rounds = filtered.map((r, i) =>
        i === 0 && r.type_vaccination === ETypeVaccine.ReVaccination
          ? sanitizeRound({ ...r, type_vaccination: ETypeVaccine.Vaccination })
          : r,
      );
      return { ...item, rounds };
    });
    void formik.setFieldValue('age_shedule', newAgeShedules);

    const newLen = (formik.values.age_shedule[selectedAgeShedule]?.rounds.length ?? 1) - 1;
    setSelectedRound((prev) => Math.max(0, Math.min(newLen - 1, prev)));
  };

  const handleDeleteAgeShedule = () => {
    const list = formik.values.age_shedule.map((s) => ({ ...s }));
    if (selectedAgeShedule <= 0 || selectedAgeShedule >= list.length) return;

    const hasNext = selectedAgeShedule < list.length - 1;
    if (hasNext) {
      list[selectedAgeShedule - 1] = { ...list[selectedAgeShedule - 1], end: list[selectedAgeShedule].end };
    }
    list.splice(selectedAgeShedule, 1);

    void formik.setFieldValue('age_shedule', list);
    setSelectedAgeShedule((prev) => Math.max(0, prev - 1));
  };

  const handleChangeCountry = (_: any, v: any) => formik.setFieldValue('manufacturerCountry', v?.name ?? '');

  const handleChangeRoundField = (fieldName: keyof TVaccineRound, value: any) => {
    const newAgeShedules = formik.values.age_shedule.map((item, asIdx) => {
      if (asIdx !== selectedAgeShedule) return item;
      return {
        ...item,
        rounds: item.rounds.map((r, rIdx) =>
          rIdx === selectedRound ? sanitizeRound({ ...r, [fieldName]: value }) : r,
        ),
      };
    });
    void formik.setFieldValue('age_shedule', newAgeShedules);
  };

  useEffect(() => {
    if (isOpen) {
      formik.resetForm({
        values: selectedVaccine
          ? { ...selectedVaccine, age_shedule: normalizeAgeShedulePeriodic(selectedVaccine.age_shedule) }
          : initialValues,
      });
      codeCheck.reset();
    } else {
      setSelectedAgeShedule(0);
      setSelectedRound(0);
      setEmptyRoundsConfirm({ open: false, periods: [], values: null });
    }
  }, [isOpen, selectedVaccine]);

  useEffect(() => {
    if (isOpen && formik.values.age_shedule.length === 0) {
      handleAddAgeShedule();
    }
  }, [isOpen]);

  useEffect(() => setSelectedRound(0), [selectedAgeShedule]);

  return (
    <Dialog
      open={isOpen}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 'min(1000px, 70vw)',
          maxHeight: '85vh',
        },
      }}
    >
      <form onSubmit={formik.handleSubmit} style={{ paddingRight: '8px' }}>
        <DialogTitle>
          {title}
          <Box display="flex" alignItems="center">
            <Typography sx={{ color: formik.values.verified ? '#2e7d32' : '#ed6c02' }}>
              {formik.values.verified ? 'Verified' : 'Not verified'}
            </Typography>
            {formik.values.verified ? <TaskAlt color="success" /> : <ReportGmailerrorred color="warning" />}
          </Box>
        </DialogTitle>

        <DialogContent>
          <TextField
            label="Name (EN)"
            fullWidth
            multiline
            margin="dense"
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            error={isExistVaccine}
            helperText={isExistVaccine && 'Вакцина с таким названием уже добавлена!'}
          />
          <TextField
            label="Name (RU)"
            fullWidth
            multiline
            margin="dense"
            name="name_ru"
            value={formik.values.name_ru}
            onChange={formik.handleChange}
            error={isExistVaccine}
            helperText={isExistVaccine && 'Вакцина с таким названием уже добавлена!'}
          />
          <TextField
            label="Code"
            fullWidth
            margin="dense"
            name="code"
            value={formik.values.code}
            onChange={formik.handleChange}
            error={codeCheck.isDuplicate ? true : formik.touched.code && Boolean(formik.errors.code)}
            helperText={
              codeCheck.isDuplicate ? 'This code is already in use' : formik.touched.code && formik.errors.code
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {codeCheck.isChecking && <CircularProgress size={20} />}
                  {codeCheck.isUnique && <CheckCircle color="success" />}
                  {codeCheck.isDuplicate && <Cancel color="error" />}
                </InputAdornment>
              ),
            }}
          />
          <Autocomplete
            fullWidth
            options={pharmaCompanies}
            value={pharmaCompanies.find((c) => c.id === formik.values.pharmaceuticalCompanyId) ?? null}
            getOptionLabel={(option) => option.abbreviatedName}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, newValue) => {
              if (newValue === null) {
                void formik.setFieldValue('pharmaceuticalCompanyId', undefined);
                void formik.setFieldValue('manufacturer', '');
                void formik.setFieldValue('manufacturerCountry', '');
                return;
              }
              void formik.setFieldValue('pharmaceuticalCompanyId', newValue.id);
              void formik.setFieldValue('manufacturer', newValue.abbreviatedName);
              const matchedCountry = countries.find((c) => c.code === newValue.country);
              void formik.setFieldValue('manufacturerCountry', matchedCountry?.name ?? '');
            }}
            renderOption={(props, option) => (
              <Box
                component="li"
                sx={{ '& > img': { mr: 1.5, flexShrink: 0 }, display: 'flex', alignItems: 'center' }}
                {...props}
                key={option.id}
              >
                {option.country && (
                  <img
                    loading="lazy"
                    width="20"
                    src={`https://flagcdn.com/w20/${option.country.toLowerCase()}.png`}
                    srcSet={`https://flagcdn.com/w40/${option.country.toLowerCase()}.png 2x`}
                    alt=""
                  />
                )}
                <Box component="span" sx={{ fontWeight: 500 }}>
                  {option.abbreviatedName}
                </Box>
                {option.country && (
                  <Box component="span" sx={{ ml: 1, color: 'text.secondary', fontSize: '0.85em' }}>
                    ({option.country})
                  </Box>
                )}
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Pharmaceutical company"
                margin="dense"
                placeholder="Search pharmaceutical company..."
              />
            )}
          />
          <TextField
            label="Manufacturing"
            fullWidth
            margin="dense"
            name="manufacturer"
            value={formik.values.manufacturer}
            onChange={formik.handleChange}
            disabled={Boolean(formik.values.pharmaceuticalCompanyId)}
            error={isExistVaccine}
            helperText={
              formik.values.pharmaceuticalCompanyId
                ? 'Auto-filled from selected pharmaceutical company'
                : isExistVaccine && 'Вакцина с таким производителем уже добавлена!'
            }
          />
          <AutocompleteCountry
            label="Manufacturer country"
            value={formik.values.manufacturerCountry}
            countries={countries}
            onChange={handleChangeCountry}
            disabled={Boolean(formik.values.pharmaceuticalCompanyId)}
            error={isExistVaccine}
            helperText={
              formik.values.pharmaceuticalCompanyId
                ? 'Auto-filled from selected pharmaceutical company'
                : isExistVaccine && 'Вакцина с такой страной производителем уже добавлена!'
            }
          />
          <AutocompleteCheckboxesCountry
            label="Certified in Countries"
            multiple={true}
            name="availableCountries"
            options={countries}
            values={formik.values.availableCountries}
            onChange={formik.setFieldValue}
          />
          {!isCreateMode && selectedVaccine?.diseases && selectedVaccine.diseases.length > 0 && (
            <TextField
              label="Used in diseases"
              fullWidth
              margin="dense"
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <Box display="flex" flexWrap="wrap" gap={0.5} sx={{ py: 0.5 }}>
                    {selectedVaccine.diseases.map((disease: VaccineDiseaseRef) => (
                      <Box
                        key={disease.id}
                        component="span"
                        display="inline-flex"
                        alignItems="center"
                        gap={0.3}
                        px={0.5}
                        py={0.85}
                        sx={{
                          backgroundColor: 'transparent',
                          borderRadius: '4px',
                          whiteSpace: 'nowrap',
                          fontSize: '0.875rem',
                          color: disease.verified ? '#29b194' : 'inherit',
                        }}
                      >
                        {disease.verified ? (
                          <TaskAlt sx={{ fontSize: 16 }} color="success" />
                        ) : (
                          <ReportGmailerrorred sx={{ fontSize: 16 }} color="warning" />
                        )}
                        {disease.name_en}
                      </Box>
                    ))}
                  </Box>
                ),
                inputProps: { style: { display: 'none' } },
              }}
            />
          )}
          <FormControl fullWidth margin="dense">
            <InputLabel>Type of vaccine</InputLabel>
            <Select
              label="Type of vaccine"
              value={formik.values.vaccine_type}
              name={`vaccine_type`}
              onChange={formik.handleChange}
            >
              {Object.entries(EVaccineType).map(([key, value]) => (
                <MenuItem key={key} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box ref={ageSheduleRef} sx={{ backgroundColor: 'rgba(41,177,148, 0.15)', scrollMarginTop: 8 }} p={2}>
            <Typography fontWeight="bold">Age Shedule</Typography>
            <Box display="flex" alignItems="center" flexWrap="wrap" my={1} rowGap={1}>
              {formik.values.age_shedule.map((item, index) => (
                <Box key={`${index}_${item.start}_age_shedule_chip`} display="flex" alignItems="center">
                  {index > 0 &&
                    (() => {
                      const canInsert = canInsertAgeShedule(index);
                      return (
                        <Tooltip
                          title={canInsert ? 'Insert period between' : 'Neighboring periods are too small to split'}
                          arrow
                        >
                          <Box
                            sx={{
                              width: 24,
                              height: 32,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: canInsert ? 'pointer' : 'not-allowed',
                              zIndex: 1,
                              borderRadius: '50%',
                              '& .insert-icon': {
                                opacity: canInsert ? 0.3 : 0.15,
                                transition: 'all 0.2s',
                                fontSize: 16,
                                color: canInsert ? '#29b194' : 'rgba(0, 0, 0, 0.4)',
                              },
                              '&:hover .insert-icon': canInsert ? { opacity: 1, transform: 'scale(1.3)' } : {},
                            }}
                            onClick={canInsert ? () => handleInsertAgeShedule(index) : undefined}
                          >
                            <Add className="insert-icon" />
                          </Box>
                        </Tooltip>
                      );
                    })()}
                  <Badge
                    invisible={(item.rounds?.length ?? 0) > 0}
                    overlap="rectangular"
                    badgeContent={
                      <Tooltip title="No rounds in this period" arrow>
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            backgroundColor: '#f59e0b',
                            color: 'white',
                            fontSize: 11,
                            fontWeight: 700,
                            lineHeight: 1,
                            cursor: 'help',
                            boxShadow: '0 0 0 2px rgba(245, 236, 219, 1)',
                          }}
                        >
                          !
                        </Box>
                      </Tooltip>
                    }
                    sx={{
                      '& .MuiBadge-badge': {
                        padding: 0,
                        minWidth: 0,
                        height: 'auto',
                        backgroundColor: 'transparent',
                        right: 4,
                        top: 4,
                      },
                    }}
                  >
                    <Chip
                      label={`${item.start}-${item.end}`}
                      onClick={() => setSelectedAgeShedule(index)}
                      sx={{
                        backgroundColor: selectedAgeShedule === index ? '#29b194' : 'unset',
                        color: selectedAgeShedule === index ? 'white' : '',
                        borderRadius: '6px',
                        border:
                          selectedAgeShedule !== index
                            ? (item.rounds?.length ?? 0) === 0
                              ? '1px dashed rgba(0, 0, 0, 0.4)'
                              : '1px solid rgba(0, 0, 0, 0.3)'
                            : '',
                      }}
                      clickable={selectedAgeShedule !== index}
                    />
                  </Badge>
                </Box>
              ))}
              <Tooltip title="Add period at end" arrow>
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    ml: formik.values.age_shedule.length > 0 ? '4px' : 0,
                    px: 0.5,
                    minWidth: 'auto',
                    color: 'rgba(58, 58, 58, 1)',
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    boxShadow: 'none',
                    ':hover': { backgroundColor: 'rgba(0, 0, 0, 0.07)', boxShadow: 'none' },
                  }}
                  onClick={handleAddAgeShedule}
                >
                  <Add />
                </Button>
              </Tooltip>
            </Box>
            {formik.values.age_shedule.length > 0 && (
              <Box mb={2}>
                <Box display="flex" justifyContent="space-between">
                  <TextField
                    sx={{ width: '49%' }}
                    margin="dense"
                    label="Start (in days)*"
                    type="number"
                    value={formik.values.age_shedule[selectedAgeShedule]?.start ?? ''}
                    onChange={(e) => {
                      if (e.target.value === '') {
                        void formik.setFieldValue(
                          'age_shedule',
                          formik.values.age_shedule.map((s, i) => (i === selectedAgeShedule ? { ...s, start: '' } : s)),
                        );
                      } else {
                        handleAgeSheduleStartChange(selectedAgeShedule, Number(e.target.value));
                      }
                    }}
                    onBlur={() => handleAgeSheduleStartBlur(selectedAgeShedule)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), (e.target as HTMLElement).blur())}
                    error={
                      formik.values.age_shedule[selectedAgeShedule]?.start === undefined ||
                      `${formik.values.age_shedule[selectedAgeShedule]?.start}` === ''
                    }
                    helperText={
                      (formik.values.age_shedule[selectedAgeShedule]?.start === undefined ||
                        `${formik.values.age_shedule[selectedAgeShedule]?.start}` === '') &&
                      'Field is required!'
                    }
                  />
                  <TextField
                    sx={{ width: '49%' }}
                    margin="dense"
                    label="End (in days)*"
                    type="number"
                    value={formik.values.age_shedule[selectedAgeShedule]?.end ?? ''}
                    onChange={(e) => {
                      if (e.target.value === '') {
                        void formik.setFieldValue(
                          'age_shedule',
                          formik.values.age_shedule.map((s, i) => (i === selectedAgeShedule ? { ...s, end: '' } : s)),
                        );
                      } else {
                        handleAgeSheduleEndChange(selectedAgeShedule, Number(e.target.value));
                      }
                    }}
                    onBlur={() => handleAgeSheduleEndBlur(selectedAgeShedule)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), (e.target as HTMLElement).blur())}
                    error={
                      formik.values.age_shedule[selectedAgeShedule]?.end === undefined ||
                      `${formik.values.age_shedule[selectedAgeShedule]?.end}` === ''
                    }
                    helperText={
                      (formik.values.age_shedule[selectedAgeShedule]?.end === undefined ||
                        `${formik.values.age_shedule[selectedAgeShedule]?.end}` === '') &&
                      'Field is required!'
                    }
                  />
                </Box>
                <Box display="flex" justifyContent={selectedAgeShedule !== 0 ? 'space-between' : 'flex-end'}>
                  {selectedAgeShedule !== 0 && (
                    <Button color="error" size="small" onClick={handleDeleteAgeShedule}>
                      DELETE THIS AGE
                    </Button>
                  )}
                  {+formik.values.age_shedule[selectedAgeShedule]?.end -
                    +formik.values.age_shedule[selectedAgeShedule]?.start >=
                    1 && (
                    <Button
                      size="small"
                      startIcon={<ContentCut sx={{ fontSize: 14 }} />}
                      onClick={() => handleSplitAgeShedule(selectedAgeShedule)}
                      sx={{ color: '#29b194' }}
                    >
                      SPLIT PERIOD
                    </Button>
                  )}
                </Box>
              </Box>
            )}
            <Box display="flex" alignItems="center" flexWrap="wrap" rowGap={1}>
              {formik.values.age_shedule[selectedAgeShedule]?.rounds.map((round, index) => (
                <Box key={index} display="flex" alignItems="center">
                  {index > 0 && (
                    <Tooltip title="Insert round between" arrow>
                      <Box
                        sx={{
                          width: 24,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          zIndex: 1,
                          borderRadius: '50%',
                          '& .insert-icon': {
                            opacity: 0.3,
                            transition: 'all 0.2s',
                            fontSize: 16,
                            color: '#29b194',
                          },
                          '&:hover .insert-icon': {
                            opacity: 1,
                            transform: 'scale(1.3)',
                          },
                        }}
                        onClick={() => handleInsertRound(index)}
                      >
                        <Add className="insert-icon" />
                      </Box>
                    </Tooltip>
                  )}
                  <Chip
                    label={`Round ${index + 1}`}
                    onClick={() => setSelectedRound(index)}
                    sx={{
                      backgroundColor: selectedRound === index ? '#29b194' : 'unset',
                      color: selectedRound === index ? 'white' : '',
                      borderRadius: '6px',
                      border: selectedRound !== index ? '1px solid rgba(0, 0, 0, 0.3)' : '',
                    }}
                    clickable={selectedRound !== index}
                  />
                </Box>
              ))}
              {!(
                formik.values.age_shedule[selectedAgeShedule]?.rounds.at(-1)?.type_vaccination ===
                  ETypeVaccine.ReVaccination &&
                formik.values.age_shedule[selectedAgeShedule]?.rounds.at(-1)?.isPeriodicRevactination
              ) && (
                <Tooltip title="Add round at end" arrow>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{
                      ml: formik.values.age_shedule[selectedAgeShedule]?.rounds.length > 0 ? '4px' : 0,
                      px: 0.5,
                      minWidth: 'auto',
                      color: 'rgba(58, 58, 58, 1)',
                      backgroundColor: 'rgba(0, 0, 0, 0.05)',
                      boxShadow: 'none',
                      ':hover': { backgroundColor: 'rgba(0, 0, 0, 0.07)', boxShadow: 'none' },
                    }}
                    onClick={handleAddRound}
                  >
                    <Add />
                  </Button>
                </Tooltip>
              )}
            </Box>
            {(formik.values.age_shedule[selectedAgeShedule]?.rounds.length ?? 0) === 0 && (
              <Box
                mt={2}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  minHeight: 140,
                  borderRadius: '6px',
                  border: '1px dashed rgba(41, 177, 148, 0.5)',
                  backgroundColor: 'rgba(255, 255, 255, 0.4)',
                }}
              >
                <EventBusyOutlined sx={{ fontSize: 28, color: 'rgba(0, 0, 0, 0.35)' }} />
                <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.7)' }}>
                  No rounds in this period
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<Add sx={{ fontSize: 16 }} />}
                  onClick={handleAddRound}
                  sx={{
                    color: '#29b194',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    ':hover': { backgroundColor: 'rgba(41,177,148,0.08)' },
                  }}
                >
                  Add round
                </Button>
              </Box>
            )}
            {formik.values.age_shedule[selectedAgeShedule]?.rounds[selectedRound] &&
              [formik.values.age_shedule[selectedAgeShedule]?.rounds[selectedRound]].map((round) => (
                <Box key={`${selectedAgeShedule}_${selectedRound}_age_shedule_rounds`} mt={2}>
                  <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                    <RadioGroup
                      row
                      aria-labelledby="type_vaccination"
                      name={`age_shedule[${selectedAgeShedule}].rounds[${selectedRound}].type_vaccination`}
                      value={formik.values.age_shedule[selectedAgeShedule].rounds[selectedRound]?.type_vaccination}
                      onChange={(e) => handleChangeRoundField('type_vaccination', e.target.value)}
                    >
                      {(() => {
                        const rounds = formik.values.age_shedule[selectedAgeShedule]?.rounds ?? [];
                        const hasPriorRevaccination = rounds
                          .slice(0, selectedRound)
                          .some((r) => r.type_vaccination === ETypeVaccine.ReVaccination);
                        const hasLaterVaccination = rounds
                          .slice(selectedRound + 1)
                          .some((r) => r.type_vaccination === ETypeVaccine.Vaccination);
                        const reVaccinationTooltip =
                          selectedRound === 0
                            ? 'The first dose cannot be a revaccination'
                            : hasLaterVaccination
                            ? 'Change later vaccination rounds first'
                            : '';
                        return (
                          <>
                            <Tooltip
                              title={hasPriorRevaccination ? 'Vaccination cannot follow a revaccination round' : ''}
                              arrow
                              disableHoverListener={!hasPriorRevaccination}
                            >
                              <span>
                                <FormControlLabel
                                  value={ETypeVaccine.Vaccination}
                                  control={<Radio />}
                                  label="Vaccination"
                                  disabled={hasPriorRevaccination}
                                />
                              </span>
                            </Tooltip>
                            <Tooltip title={reVaccinationTooltip} arrow disableHoverListener={!reVaccinationTooltip}>
                              <span>
                                <FormControlLabel
                                  value={ETypeVaccine.ReVaccination}
                                  control={<Radio />}
                                  label="Revaccination"
                                  disabled={selectedRound === 0 || hasLaterVaccination}
                                />
                              </span>
                            </Tooltip>
                          </>
                        );
                      })()}
                    </RadioGroup>
                    {(() => {
                      const rounds = formik.values.age_shedule[selectedAgeShedule]?.rounds ?? [];
                      const round = rounds[selectedRound];
                      const isLastRound = selectedRound === rounds.length - 1;
                      return (
                        round?.type_vaccination === ETypeVaccine.ReVaccination &&
                        isLastRound && (
                          <FormControlLabel
                            control={<Checkbox />}
                            label="Periodically"
                            name={`age_shedule[${selectedAgeShedule}].rounds[${selectedRound}].isPeriodicRevactination`}
                            checked={!!round?.isPeriodicRevactination}
                            onChange={(e: any) => handleChangeRoundField('isPeriodicRevactination', e.target.checked)}
                          />
                        )
                      );
                    })()}
                  </Box>
                  <SelectInput
                    label="Type round (date vaccination)"
                    name={`age_shedule[${selectedAgeShedule}].rounds[${selectedRound}].type_round`}
                    value={formik.values.age_shedule[selectedAgeShedule].rounds[selectedRound]?.type_round}
                    menuItem={Object.entries(ENextRound)
                      .filter((_, index) => {
                        if (selectedRound === 0 && index < 2) return true;
                        if (selectedRound === 1 && index > 0 && index < 3) return true;
                        if (selectedRound > 1 && index > 0) return true;

                        return false;
                      })
                      .map(([value, label]) => ({ label, value }))}
                    onChange={(_: any, value: any) => handleChangeRoundField('type_round', value)}
                  />
                  {(formik.values.age_shedule[selectedAgeShedule].rounds[selectedRound]?.type_vaccination ===
                    ETypeVaccine.Vaccination ||
                    (formik.values.age_shedule[selectedAgeShedule].rounds[selectedRound]?.type_vaccination ===
                      ETypeVaccine.ReVaccination &&
                      !formik.values.age_shedule[selectedAgeShedule].rounds[selectedRound]?.isPeriodicRevactination)) &&
                    formik.values.age_shedule[selectedAgeShedule].rounds[selectedRound]?.type_round !==
                      ENextRoundVariant.AnyDays && (
                      <Box display="flex" justifyContent="space-between">
                        <TextField
                          sx={{ width: '49%' }}
                          margin="dense"
                          label="Start (in days)"
                          type="number"
                          name={`age_shedule[${selectedAgeShedule}].rounds[${selectedRound}].count_days`}
                          value={formik.values.age_shedule[selectedAgeShedule].rounds[selectedRound]?.count_days ?? ''}
                          onChange={(e) => handleChangeRoundField('count_days', e.target.value)}
                        />
                        <TextField
                          sx={{ width: '49%' }}
                          margin="dense"
                          label="End (in days)"
                          type="number"
                          name={`age_shedule[${selectedAgeShedule}].rounds[${selectedRound}].count_days2`}
                          value={formik.values.age_shedule[selectedAgeShedule].rounds[selectedRound]?.count_days2 ?? ''}
                          onChange={(e) => handleChangeRoundField('count_days2', e.target.value)}
                        />
                      </Box>
                    )}
                  {formik.values.age_shedule[selectedAgeShedule].rounds[selectedRound]?.type_vaccination ===
                    ETypeVaccine.ReVaccination &&
                    formik.values.age_shedule[selectedAgeShedule].rounds[selectedRound]?.isPeriodicRevactination && (
                      <FormControl fullWidth margin="dense">
                        <InputLabel>Period</InputLabel>
                        <Select
                          label="Age"
                          value={formik.values.age_shedule[selectedAgeShedule].rounds[selectedRound]?.count_years}
                          name={`age_shedule[${selectedAgeShedule}].rounds[${selectedRound}].count_years`}
                          onChange={(e: any) => handleChangeRoundField('count_years', e.target.value)}
                        >
                          <MenuItem value={1}>Every year</MenuItem>
                          <MenuItem value={2}>Once every 2 years</MenuItem>
                          <MenuItem value={3}>Once every 3 years</MenuItem>
                          <MenuItem value={5}>Once every 5 years</MenuItem>
                          <MenuItem value={10}>Once every 10 years</MenuItem>
                          <MenuItem value={15}>Once every 15 years</MenuItem>
                          <MenuItem value={20}>Once every 20 years</MenuItem>
                          <MenuItem value={25}>Once every 25 years</MenuItem>
                          <MenuItem value={30}>Once every 30 years</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  {(formik.values.age_shedule[selectedAgeShedule]?.rounds.length ?? 0) > 0 && (
                    <Box display="flex" justifyContent="flex-end">
                      <Button color="error" size="small" onClick={handleDeleteRound}>
                        DELETE THIS ROUND
                      </Button>
                    </Box>
                  )}
                </Box>
              ))}
          </Box>
          <TextField
            label="Descriptions (EN)"
            fullWidth
            sx={{ mt: 4 }}
            margin="dense"
            multiline
            name="descriptions_en"
            value={formik.values.descriptions_en ?? selectedVaccine?.descriptions_en}
            onChange={formik.handleChange}
          />
          <TextField
            label="Descriptions (RU)"
            fullWidth
            margin="dense"
            multiline
            name="descriptions"
            value={formik.values.descriptions ?? selectedVaccine?.descriptions}
            onChange={formik.handleChange}
          />
          <TextField
            label="Pharmacological properties"
            fullWidth
            margin="dense"
            multiline
            name="pharmacological_properties"
            value={formik.values.pharmacological_properties ?? selectedVaccine?.pharmacological_properties}
            onChange={formik.handleChange}
          />
          <TextField
            label="Compound"
            fullWidth
            margin="dense"
            multiline
            name="compound"
            value={formik.values.compound ?? selectedVaccine?.compound}
            onChange={formik.handleChange}
          />
          <TextField
            label="Indications for use"
            fullWidth
            margin="dense"
            multiline
            name="indications_for_use"
            value={formik.values.indications_for_use ?? selectedVaccine?.indications_for_use}
            onChange={formik.handleChange}
          />
          <TextField
            label="Contraindications"
            fullWidth
            margin="dense"
            multiline
            name="contraindications"
            value={formik.values.contraindications ?? selectedVaccine?.contraindications}
            onChange={formik.handleChange}
          />
          <TextField
            label="Mode of application"
            fullWidth
            margin="dense"
            multiline
            name="mode_of_application"
            value={formik.values.mode_of_application ?? selectedVaccine?.mode_of_application}
            onChange={formik.handleChange}
          />
          <TextField
            label="Precautions for use"
            fullWidth
            margin="dense"
            multiline
            name="precautions_for_use"
            value={formik.values.precautions_for_use ?? selectedVaccine?.precautions_for_use}
            onChange={formik.handleChange}
          />
          <TextField
            label="Carefully"
            fullWidth
            margin="dense"
            multiline
            name="carefully"
            value={formik.values.carefully ?? selectedVaccine?.carefully}
            onChange={formik.handleChange}
          />
          <TextField
            label="Special instructions"
            fullWidth
            margin="dense"
            multiline
            name="special_instructions"
            value={formik.values.special_instructions ?? selectedVaccine?.special_instructions}
            onChange={formik.handleChange}
          />
          <TextField
            label="Side effect"
            fullWidth
            margin="dense"
            multiline
            name="side_effect"
            value={formik.values.side_effect ?? selectedVaccine?.side_effect}
            onChange={formik.handleChange}
          />
          <TextField
            label="Use during pregnancy and breastfeeding"
            fullWidth
            margin="dense"
            multiline
            name="use_during_pregnancy_and_breastfeeding"
            value={
              formik.values.use_during_pregnancy_and_breastfeeding ??
              selectedVaccine?.use_during_pregnancy_and_breastfeeding
            }
            onChange={formik.handleChange}
          />
          <TextField
            label="Interaction with other drugs"
            fullWidth
            margin="dense"
            multiline
            name="interaction_with_other_drugs"
            value={formik.values.interaction_with_other_drugs ?? selectedVaccine?.interaction_with_other_drugs}
            onChange={formik.handleChange}
          />
          <TextField
            label="Storage conditions"
            fullWidth
            margin="dense"
            multiline
            name="storage_conditions"
            value={formik.values.storage_conditions ?? selectedVaccine?.storage_conditions}
            onChange={formik.handleChange}
          />
          <TextField
            label="Transportation conditions"
            fullWidth
            margin="dense"
            multiline
            name="transportation_conditions"
            value={formik.values.transportation_conditions ?? selectedVaccine?.transportation_conditions}
            onChange={formik.handleChange}
          />
          <TextField
            label="Notes"
            fullWidth
            margin="dense"
            multiline
            name="notes"
            value={formik.values.notes ?? selectedVaccine?.notes}
            onChange={formik.handleChange}
          />
        </DialogContent>
        <DialogActions sx={{ mx: 2 }}>
          {userRole === EUserRole.Admin && onVerified && (
            <Button
              onClick={() => {
                onVerified({ ...formik.values, verified: !formik.values.verified });
                onClose();
              }}
              variant="contained"
              color={formik.values.verified ? 'error' : 'primary'}
              sx={{ mr: 'auto' }}
              disabled={
                isExistVaccine ||
                codeCheck.isDuplicate ||
                !formik.isValid ||
                // Если верифицированная - кнопка Unverified всегда активна
                // Если не верифицированная - применяем проверку на изменения
                (!formik.values.verified && !isCreateMode && !formik.dirty)
              }
            >
              {formik.values.verified ? 'Unverified' : 'Save and Verified'}
            </Button>
          )}
          <Button onClick={onClose} sx={{ mr: 8 }}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            disabled={
              isExistVaccine ||
              codeCheck.isDuplicate ||
              !formik.isValid ||
              // Если не верифицированная - проверяем изменения
              // Если верифицированная - кнопка Save следует обычной логике (нужны изменения)
              (!isCreateMode && !formik.dirty)
            }
            loading={isLoading}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </form>
      <Dialog
        open={emptyRoundsConfirm.open}
        onClose={handleEmptyRoundsCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              backgroundColor: 'rgba(245, 158, 11, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <WarningAmberOutlined sx={{ color: '#f59e0b', fontSize: 22 }} />
          </Box>
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            Schedule without rounds
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.7)', mb: 2 }}>
            The following schedules <strong>CONTAIN NO ROUNDS</strong>:
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              p: 1.5,
              borderRadius: 1.5,
              backgroundColor: 'rgba(245, 158, 11, 0.08)',
              mb: 2,
            }}
          >
            {emptyRoundsConfirm.periods.map((p) => (
              <Chip
                key={p.index}
                label={`${p.start}–${p.end} days`}
                size="small"
                onClick={() => handleGoToPeriod(p.index)}
                sx={{
                  backgroundColor: 'white',
                  border: '1px dashed #f59e0b',
                  color: '#92500a',
                  fontWeight: 500,
                  borderRadius: '6px',
                }}
              />
            ))}
          </Box>
          <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.85)' }}>
            Is this intentional?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleEmptyRoundsCancel} sx={{ color: 'rgba(0,0,0,0.6)' }}>
            No, go back
          </Button>
          <Button
            variant="contained"
            onClick={handleEmptyRoundsConfirm}
            sx={{
              backgroundColor: '#29b194',
              ':hover': { backgroundColor: '#1f8a72' },
              textTransform: 'uppercase',
            }}
          >
            Yes, save
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};
